import { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { api } from "@shared/routes";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { normalizarTagParceiro, tagParceiroValida } from "@/lib/tag-parceiro";
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  X,
  Download,
  Loader2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ParceiroRow {
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj?: string;
  cpf?: string;
  email: string;
  uf: string;
  cidade: string;
  telefone?: string;
  idProprio?: number;
  tagParceiro: string;
}

interface ImportResult {
  criados: number;
  ignorados: number;
  erros: { linha: number; razao: string }[];
}

// ---------------------------------------------------------------------------
// Column aliases (case-insensitive)
// ---------------------------------------------------------------------------
const COLUMN_ALIASES: Record<keyof ParceiroRow, string[]> = {
  razaoSocial: ["razaosocial", "razão social", "razao_social", "razão_social", "razao social", "nome", "empresa"],
  nomeFantasia: ["nomefantasia", "nome fantasia", "nome_fantasia", "fantasia"],
  cnpj: ["cnpj"],
  cpf: ["cpf"],
  email: ["email", "e-mail", "e_mail"],
  uf: ["uf", "estado", "state"],
  cidade: ["cidade", "city"],
  telefone: ["telefone", "fone", "phone", "tel"],
  idProprio: ["idproprio", "id_proprio", "id próprio", "id proprio", "código", "codigo", "cod", "id"],
  tagParceiro: ["tag", "tag parceiro", "tag_parceiro", "tagparceiro", "tag do parceiro"],
};

function normalizeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

function mapHeader(header: string): keyof ParceiroRow | null {
  const norm = normalizeKey(header);
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    if (aliases.includes(norm)) return field as keyof ParceiroRow;
  }
  return null;
}

// ---------------------------------------------------------------------------
// CSV parser (handles quoted fields)
// ---------------------------------------------------------------------------
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cell += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === "," || ch === ";") { row.push(cell); cell = ""; }
      else if (ch === "\n" || (ch === "\r" && text[i + 1] === "\n")) {
        if (ch === "\r") i++;
        row.push(cell); cell = "";
        if (row.some(c => c.trim() !== "")) rows.push(row);
        row = [];
      } else { cell += ch; }
    }
  }
  if (cell !== "" || row.length > 0) { row.push(cell); if (row.some(c => c.trim() !== "")) rows.push(row); }
  return rows;
}

// ---------------------------------------------------------------------------
// Parse file → ParceiroRow[]
// ---------------------------------------------------------------------------
async function parseFile(file: File): Promise<{ rows: ParceiroRow[]; warnings: string[] }> {
  const warnings: string[] = [];
  let rawRows: string[][];

  if (file.name.endsWith(".csv") || file.type === "text/csv") {
    const text = await file.text();
    rawRows = parseCSV(text);
  } else {
    // XLSX / XLS
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const data: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as string[][];
    rawRows = data.map(r => r.map(c => String(c ?? "")));
  }

  if (rawRows.length < 2) throw new Error("Arquivo vazio ou sem dados além do cabeçalho.");

  // Map header row
  const headerRow = rawRows[0];
  const fieldMap: Record<number, keyof ParceiroRow> = {};
  for (let col = 0; col < headerRow.length; col++) {
    const field = mapHeader(headerRow[col]);
    if (field) fieldMap[col] = field;
  }

  const required: (keyof ParceiroRow)[] = ["razaoSocial", "email", "uf", "cidade", "tagParceiro"];
  for (const req of required) {
    if (!Object.values(fieldMap).includes(req)) {
      throw new Error(`Coluna obrigatória não encontrada: "${req}". Verifique o cabeçalho do arquivo.`);
    }
  }

  const rows: ParceiroRow[] = [];

  for (let i = 1; i < rawRows.length; i++) {
    const dataRow = rawRows[i];
    if (dataRow.every(c => c.trim() === "")) continue;

    const obj: Partial<ParceiroRow> = {};
    for (const [colStr, field] of Object.entries(fieldMap)) {
      const col = Number(colStr);
      const val = dataRow[col]?.trim() ?? "";
      if (val === "") continue;
      if (field === "idProprio") {
        const num = parseInt(val, 10);
        if (!isNaN(num)) (obj as any)[field] = num;
      } else if (field === "tagParceiro") {
        (obj as any)[field] = normalizarTagParceiro(val);
      } else {
        (obj as any)[field] = val;
      }
    }

    if (!obj.razaoSocial) { warnings.push(`Linha ${i + 1}: Razão Social vazia — ignorada`); continue; }
    if (!obj.email) { warnings.push(`Linha ${i + 1}: E-mail vazio — ignorada`); continue; }
    if (!obj.uf) { warnings.push(`Linha ${i + 1}: UF vazia — ignorada`); continue; }
    if (!obj.cidade) { warnings.push(`Linha ${i + 1}: Cidade vazia — ignorada`); continue; }

    rows.push({ ...obj, tagParceiro: obj.tagParceiro ?? "" } as ParceiroRow);
  }

  if (rows.length === 0) throw new Error("Nenhuma linha válida encontrada no arquivo.");

  return { rows, warnings };
}

// ---------------------------------------------------------------------------
// Download template helper
// ---------------------------------------------------------------------------
function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    ["razaoSocial", "nomeFantasia", "cnpj", "cpf", "email", "uf", "cidade", "telefone", "idProprio", "tagParceiro"],
    ["Empresa Exemplo LTDA", "Exemplo", "00.000.000/0001-00", "", "contato@exemplo.com.br", "SP", "São Paulo", "(11) 99999-9999", "1001", "PRM"],
  ]);
  ws["!cols"] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 30 }, { wch: 6 }, { wch: 20 }, { wch: 18 }, { wch: 12 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws, "Parceiros");
  XLSX.writeFile(wb, "modelo_importacao_parceiros.xlsx");
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = "upload" | "preview" | "result";

export function ImportarParceirosDialog({ open, onClose }: Props) {
  const { session } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("upload");
  const [dragging, setDragging] = useState(false);
  const [rows, setRows] = useState<ParceiroRow[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("upload");
    setRows([]);
    setParseWarnings([]);
    setParseError(null);
    setFileName("");
    setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = useCallback(async (file: File) => {
    setParseError(null);
    setFileName(file.name);
    try {
      const { rows: parsed, warnings } = await parseFile(file);
      setRows(parsed);
      setParseWarnings(warnings);
      setStep("preview");
    } catch (e: any) {
      setParseError(e.message ?? "Erro ao processar arquivo.");
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const importMutation = useMutation({
    mutationFn: async (parceiros: ParceiroRow[]) => {
      const res = await fetch("/api/empresas/batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ parceiros }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (Array.isArray(err.erros)) {
          return {
            criados: Number(err.criados ?? 0),
            ignorados: Number(err.ignorados ?? 0),
            erros: err.erros as ImportResult["erros"],
          };
        }
        throw new Error(err.message || "Erro ao importar");
      }
      return res.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
      setResult(data);
      setStep("result");
      if (data.criados > 0) {
        toast({ title: `${data.criados} parceiro(s) importado(s) com sucesso!` });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Erro na importação", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Importar Parceiros em Lote
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP: UPLOAD ── */}
        {step === "upload" && (
          <div className="space-y-4 flex-1">
            {/* Template download */}
            <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Modelo de planilha</p>
                <p className="text-xs text-muted-foreground">
                  Baixe o modelo com as colunas corretas antes de importar
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Baixar modelo
              </Button>
            </div>

            {/* Columns info */}
            <div className="rounded-lg border px-4 py-3 text-sm space-y-1">
              <p className="font-medium text-muted-foreground mb-2">Colunas aceitas:</p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-xs">
                <span><strong>razaoSocial</strong> — obrigatório</span>
                <span>nomeFantasia — opcional</span>
                <span>cnpj — opcional</span>
                <span>cpf — opcional</span>
                <span><strong>email</strong> — obrigatório</span>
                <span><strong>uf</strong> — obrigatório (ex: SP)</span>
                <span><strong>cidade</strong> — obrigatório</span>
                <span>telefone — opcional</span>
                <span>idProprio — opcional</span>
                <span><strong>tagParceiro</strong> — obrigatório (1–10 letras/dígitos; começa por letra)</span>
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center gap-3 py-12 ${
                dragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-10 h-10 text-muted-foreground" />
              <div className="text-center">
                <p className="font-medium">Arraste o arquivo aqui ou clique para selecionar</p>
                <p className="text-xs text-muted-foreground mt-1">CSV ou XLSX (Excel)</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
            </div>

            {parseError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{parseError}</span>
              </div>
            )}
          </div>
        )}

        {/* ── STEP: PREVIEW ── */}
        {step === "preview" && (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{fileName}</span>
                <Badge variant="secondary">{rows.length} registro(s)</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={reset}>
                <X className="w-4 h-4 mr-1" /> Trocar arquivo
              </Button>
            </div>

            {parseWarnings.length > 0 && (
              <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 space-y-0.5">
                <p className="font-medium">Avisos durante leitura:</p>
                {parseWarnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}

            {/* Preview table */}
            <div className="flex-1 overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Razão Social</th>
                    <th className="px-3 py-2 text-left font-medium">E-mail</th>
                    <th className="px-3 py-2 text-left font-medium">UF</th>
                    <th className="px-3 py-2 text-left font-medium">Cidade</th>
                    <th className="px-3 py-2 text-left font-medium">CNPJ/CPF</th>
                    <th className="px-3 py-2 text-left font-medium">ID</th>
                    <th className="px-3 py-2 text-left font-medium">TAG do Parceiro *</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-muted/20">
                      <td className="px-3 py-2 text-muted-foreground">{i + 2}</td>
                      <td className="px-3 py-2 font-medium max-w-[180px] truncate">{row.razaoSocial}</td>
                      <td className="px-3 py-2 max-w-[160px] truncate text-muted-foreground">{row.email}</td>
                      <td className="px-3 py-2">{row.uf}</td>
                      <td className="px-3 py-2 max-w-[120px] truncate">{row.cidade}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.cnpj || row.cpf || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.idProprio ?? "—"}</td>
                      <td className="px-3 py-2 min-w-40">
                        <Input
                          aria-label={`TAG da linha ${i + 2}`}
                          value={row.tagParceiro}
                          maxLength={10}
                          onChange={(event) => {
                            const tag = normalizarTagParceiro(event.target.value);
                            setRows((current) => current.map((item, index) => index === i ? { ...item, tagParceiro: tag } : item));
                          }}
                          aria-invalid={!tagParceiroValida(row.tagParceiro) || rows.some((item, index) => index !== i && normalizarTagParceiro(item.tagParceiro) === normalizarTagParceiro(row.tagParceiro)) || undefined}
                        />
                        {!tagParceiroValida(row.tagParceiro) && (
                          <p role="alert" className="mt-1 text-[11px] text-destructive">TAG inválida: use 1 a 10 letras/dígitos e comece por letra.</p>
                        )}
                        {tagParceiroValida(row.tagParceiro) && rows.some((item, index) => index !== i && normalizarTagParceiro(item.tagParceiro) === normalizarTagParceiro(row.tagParceiro)) && (
                          <p role="alert" className="mt-1 text-[11px] text-destructive">TAG repetida em outra linha.</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button
                onClick={() => importMutation.mutate(rows)}
                disabled={importMutation.isPending || rows.some((row, index) => !tagParceiroValida(row.tagParceiro) || rows.some((other, otherIndex) => otherIndex !== index && normalizarTagParceiro(other.tagParceiro) === normalizarTagParceiro(row.tagParceiro)))}
              >
                {importMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importar {rows.length} parceiro(s)
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: RESULT ── */}
        {step === "result" && result && (
          <div className="flex-1 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-green-50 border-green-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-green-700">{result.criados}</p>
                <p className="text-xs text-green-600 mt-0.5">Criados</p>
              </div>
              <div className="rounded-lg border bg-yellow-50 border-yellow-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">{result.ignorados}</p>
                <p className="text-xs text-yellow-600 mt-0.5">Ignorados (CNPJ duplicado)</p>
              </div>
              <div className="rounded-lg border bg-red-50 border-red-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-red-700">{result.erros.length}</p>
                <p className="text-xs text-red-600 mt-0.5">Erros</p>
              </div>
            </div>

            {result.erros.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  Linhas com erro:
                </p>
                <div className="space-y-1 max-h-40 overflow-auto text-xs">
                  {result.erros.map((e, i) => (
                    <p key={i} className="text-destructive">
                      Linha {e.linha}: {e.razao}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {result.criados > 0 && result.erros.length === 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Importação concluída com sucesso!
              </div>
            )}

            <div className="flex justify-end gap-2">
              {result.erros.length > 0 && (
                <Button variant="outline" onClick={() => setStep("preview")}>
                  Corrigir linhas
                </Button>
              )}
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
